import { useEffect, useState, memo, useCallback } from "react";
import useDebounce from "hooks/useDebounce";
import { Col, Row, Badge, Button, Form, Spinner } from "react-bootstrap";
import useSpeclialisedList from "hooks/useSpeclialisedList";
import { jobService } from "services/job.service";
import useCurrentUser from "hooks/useCurrentUser";
import GoogleMap from "components/GoogleMap";
import { userService } from "services";
import { defaultLocation } from "utils";
import deafultuserImg from "assets/images/avatar.svg";
import star from "assets/images/star.svg";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import PilotDetailsModal from "components/Modals/PilotDetailsModal";
import { BiStar, BiMapPin, BiUserCheck, BiGroup, BiUserX, BiX } from "react-icons/bi";

const PilotCard = memo(({ pilot, isSelected, onToggle, onViewProfile, selectedSpecializations, speclialisedList }) => (
  <div
    key={pilot._id}
    className={`pilot-card p-2 border rounded-4 transition-all cursor-pointer mb-2 ${isSelected
      ? "bg-primary-subtle border-primary shadow-sm"
      : "bg-white border-light-subtle hover-shadow"
      }`}
    onClick={onViewProfile}
    style={{
      cursor: "pointer",
      transition: "all 0.2s ease",
      transform: isSelected ? "translateX(4px)" : "none",
      borderLeft: isSelected ? "4px solid #0d6efd" : "1px solid #dee2e6",
      position: "relative"
    }}
  >
    <div className="d-flex align-items-center gap-3">
      <div
        className="selection-checkbox"
        onClick={(e) => {
          e.stopPropagation();
          onToggle(pilot._id);
        }}
      >
        <Form.Check
          type="checkbox"
          checked={isSelected}
          onChange={() => { }} // Controlled by outer div click
          style={{ cursor: "pointer", transform: "scale(1.2)" }}
        />
      </div>

      <div className="d-flex gap-2 flex-grow-1">
        <div className="position-relative">
          <img
            style={{ height: 50, width: 50 }}
            alt="User Avatar"
            className="rounded-circle border border-secondary"
            src={`${pilot?.baseS3Url}${pilot?.avatar || ""}`}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = deafultuserImg;
            }}
          />
        </div>

        <div className="flex-grow-1 overflow-hidden">
          <div className="d-flex justify-content-between align-items-start mb-0">
            <h6 className="fw-bold m-0 text-dark" style={{ fontSize: '0.85rem' }}>{pilot.fullName}</h6>
            <div className="d-flex align-items-center gap-1 text-warning">
              <BiStar size={12} />
              <img src={star} className="me-2" alt="rating" style={{ display: 'none' }} />
              <span className="fw-bold text-dark" style={{ fontSize: '0.75rem' }}>{pilot?.avgRating?.toFixed(1) || 0}</span>
            </div>
          </div>

          <div className="d-flex align-items-center gap-1 text-secondary mb-1" style={{ fontSize: '0.7rem' }}>
            <BiMapPin size={12} className="opacity-75" />
            <span className="text-truncate">{pilot.address || "Location not provided"}</span>
          </div>

          <div className="text-muted small mb-1 text-truncate" style={{ fontSize: '0.7rem', opacity: 0.8 }}>
            {(pilot?.bio?.length > 150
              ? pilot.bio.slice(0, 150) + "…"
              : pilot?.bio) || "No biography available"}

          </div>

          <div className="d-flex flex-wrap gap-1 mt-1">
            {pilot?.specializations?.slice(0, 3).map((spec, idx) => {
              const specDetail = speclialisedList?.find(s => s.title === spec || s._id === spec);
              const isMatch = selectedSpecializations.some(id => id === specDetail?._id || id === spec);

              return (
                <Badge
                  key={idx}
                  bg={isMatch ? "primary" : "secondary"}
                  className={`${isMatch ? "bg-opacity-25 text-primary" : "bg-opacity-10 text-secondary"} border-0 font-weight-normal px-2 py-1`}
                  style={{ fontSize: "0.55rem", borderRadius: "4px" }}
                >
                  {spec}
                </Badge>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  </div>
));

// --- Main Page ---

const PilotOptimization = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const jobData = state?.jobData;

  const { user: currentUser, loading: isLoading } = useCurrentUser();
  const { coordinates, pilotJobLocationRadius } = currentUser || {};

  const [location, setLocation] = useState({
    lat: jobData?.latitude || (coordinates?.length > 1 ? coordinates[1] : defaultLocation.lat),
    lng: jobData?.longitude || (coordinates?.length > 1 ? coordinates[0] : defaultLocation.lng),
  });

  const [selectedRange, setSelectedRange] = useState(pilotJobLocationRadius || 10);
  const [selectedSpecializations, setSelectedSpecializations] = useState(jobData?.specialisedId || []);
  const [pilots, setPilots] = useState([]);
  const [selectedPilots, setSelectedPilots] = useState([]);
  const [isFetchingPilots, setIsFetchingPilots] = useState(false);
  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const [mapData, setMapData] = useState(null);
  const [userAddress, setUserAddress] = useState("");

  const [showPilotModal, setShowPilotModal] = useState(false);
  const [selectedPilotToView, setSelectedPilotToView] = useState(null);

  const debouncedLocation = useDebounce(location, 500);
  const debouncedRange = useDebounce(selectedRange, 500);

  const { speclialisedList } = useSpeclialisedList();

  // Sync Initial Location when current user is loaded
  useEffect(() => {
    if (!jobData && coordinates?.length > 1) {
      setLocation({
        lat: coordinates[1],
        lng: coordinates[0],
      });
      setSelectedRange(pilotJobLocationRadius || 10);
    }
  }, [currentUser, jobData]);

  // Fetch pilots based on filters - optimized with debouncing
  useEffect(() => {
    const fetchNearbyPilots = async () => {
      setIsFetchingPilots(true);
      try {
        const payload = {
          lat: debouncedLocation.lat,
          lng: debouncedLocation.lng,
          radius: debouncedRange,
          specializationIds: selectedSpecializations,
          skip: 0,
          limit: 100,
        };
        const response = await userService.fetchPilotsWithCords(payload);
        const pilotsData = response.data?.data || response.data;
        setPilots(Array.isArray(pilotsData) ? pilotsData : pilotsData.pilots || []);
      } catch (error) {
        console.error("Error fetching pilots:", error);
        setPilots([]);
      } finally {
        setIsFetchingPilots(false);
      }
    };
    if (debouncedLocation.lat !== 0) fetchNearbyPilots();
  }, [debouncedLocation, debouncedRange, selectedSpecializations]);

  // Reset pilot selection when specializations change
  useEffect(() => {
    setSelectedPilots([]);
  }, [selectedSpecializations]);

  const handleSpecializationToggle = useCallback((id) => {
    setSelectedSpecializations(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : (prev.length < 3 ? [...prev, id] : prev)
    );
  }, []);

  const togglePilotSelection = useCallback((id) => {
    setSelectedPilots(prev => prev.includes(id) ? prev.filter(pId => pId !== id) : [...prev, id]);
  }, []);

  const handleViewProfile = useCallback((pilot) => {
    setSelectedPilotToView(pilot);
    setShowPilotModal(true);
  }, []);

  const handleSendRequest = useCallback(async (pilotIds) => {
    if (pilotIds.length === 0) {
      toast.error("No pilots selected");
      return;
    }

    setIsSendingRequest(true);
    try {
      if (jobData) {
        // Workflow: Create job WITH selected pilots
        const payload = {
          ...jobData,
          pilotIds: pilotIds
        };
        const response = await jobService.addjob(payload);
        if (response.data.success) {
          toast.success(response.data.message || "Job created and requests sent!");
          navigate('/my-jobs');
        } else {
          toast.error(response.data.message || "Failed to create job");
        }
      } else {
        toast.info("Please use the 'Request Pilot' flow from Create Job to send requests to specific pilots.");
      }
    } catch (error) {
      console.error("Error sending requests:", error);
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setIsSendingRequest(false);
    }
  }, [jobData, navigate]);

  return (
    <div className="dashboard pb-0 bg-light min-vh-100 overflow-hidden">
      <div className="container-box p-0">
        <Row className="g-0 min-vh-100 bg-white" style={{ height: "100vh" }}>
          {/* Map Column */}
          <Col lg={4} className="border-end d-none d-lg-block p-0 position-relative">
            <div className="h-100 w-100 position-sticky top-0" style={{ minHeight: "100%" }}>
              <GoogleMap
                radius={selectedRange}
                pilots={pilots}
                location={location}
                setLocation={setLocation}
                setUserAddress={setUserAddress}
                data={mapData}
                setData={setMapData}
                loading={false}
                mapTypeControl={false}
                streetViewControl={false}
                fullscreenControl={false}
                mapStyling={{ height: "100%", width: "100%" }}
              />
            </div>
          </Col>

          {/* Controls & List Column */}
          <Col lg={8} className="d-flex flex-column" style={{ height: "100vh" }}>
            <div className="flex-grow-1 overflow-auto p-3 p-md-4 custom-scrollbar">
              <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                <div>
                  <h5 className="fw-bold text-dark m-0" style={{ fontSize: '1.1rem' }}>Pilot Optimization Search</h5>
                  <p className="text-muted small m-0" style={{ fontSize: '0.75rem' }}>
                    {jobData ? `Requesting pilots for "${jobData.title}"` : `Found ${pilots.length} pilots near your location`}
                  </p>
                </div>
              </div>

              <div className="animate-fade-in mb-3">
                <Row className="g-3">
                  <Col md={12}>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label className="fw-bold m-0 small" style={{ fontSize: '0.8rem' }}>Search Radius</label>
                      <span className="text-primary fw-bold" style={{ fontSize: '0.75rem' }}>
                        {selectedRange} miles
                      </span>
                    </div>
                    <Form.Range
                      min="1"
                      max="100"
                      value={selectedRange}
                      onChange={(e) => setSelectedRange(Number(e.target.value))}
                      className="py-1"
                    />
                  </Col>
                </Row>

                <div className="mt-3">
                  <label className="fw-bold mb-1 d-block small" style={{ fontSize: '0.8rem' }}>Specializations (Up to 3)</label>
                  <div className="d-flex flex-wrap gap-1 p-2 bg-light rounded-3 border border-secondary-subtle">
                    {speclialisedList?.length > 0 && speclialisedList?.map((item) => (
                      <Badge
                        key={item._id}
                        className={`p-2 px-3 cursor-pointer border transition-all rounded-pill shadow-none ${selectedSpecializations.includes(item._id)
                          ? "bg-primary border-primary text-white"
                          : "bg-white border-secondary-subtle text-dark opacity-75"
                          }`}
                        style={{ fontSize: "0.7rem", fontWeight: "400" }}
                        onClick={() => handleSpecializationToggle(item._id)}
                      >
                        {item.title}
                        {selectedSpecializations.includes(item._id) && <BiX className="ms-1" />}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              <div className="pilot-list-section pt-2 border-top">
                <>
                  <div className="d-flex gap-2 mb-3">
                    <Button
                      variant="primary"
                      size="sm"
                      className="flex-grow-1 shadow-sm border-0 py-2 rounded-3 d-flex align-items-center justify-content-center gap-1"
                      style={{
                        background: selectedPilots.length > 0 ? "#0d6efd" : "#adb5bd",
                        fontWeight: "600",
                        fontSize: "0.8rem",
                        marginTop: 0
                      }}
                      disabled={selectedPilots.length === 0 || isSendingRequest}
                      onClick={() => handleSendRequest(selectedPilots)}
                    >
                      {isSendingRequest ? <Spinner animation="border" size="sm" /> : <BiUserCheck size={18} />}
                      {jobData ? `Confirm Selection (${selectedPilots.length})` : `Request Selected (${selectedPilots.length})`}
                    </Button>
                    {!jobData && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="flex-grow-1 shadow-sm py-2 rounded-3 d-flex align-items-center justify-content-center gap-1"
                        style={{ borderWidth: "1.5px", fontWeight: "600", fontSize: "0.8rem" }}
                        disabled={pilots.length === 0 || isSendingRequest}
                        onClick={() => handleSendRequest(pilots.map(p => p._id))}
                      >
                        {isSendingRequest ? <Spinner animation="border" size="sm" /> : <BiGroup size={18} />}
                        Request All Nearby
                      </Button>
                    )}
                  </div>

                  {isFetchingPilots ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" size="sm" />
                      <p className="mt-2 text-muted small">Searching for pilots...</p>
                    </div>
                  ) : pilots.length > 0 ? (
                    <div className="pe-1">
                      {pilots.map(pilot => (
                        <PilotCard
                          key={pilot._id}
                          pilot={pilot}
                          isSelected={selectedPilots.includes(pilot._id)}
                          onToggle={togglePilotSelection}
                          onViewProfile={() => handleViewProfile(pilot)}
                          selectedSpecializations={selectedSpecializations}
                          speclialisedList={speclialisedList}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-5 bg-light rounded-4 border">
                      <BiUserX className="text-muted" size={40} />
                      <h6 className="mt-2 text-dark" style={{ fontSize: '0.85rem' }}>No pilots found</h6>
                      <p className="text-muted small" style={{ fontSize: '0.75rem' }}>Try expanding your search radius.</p>
                    </div>
                  )}
                </>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <PilotDetailsModal
        show={showPilotModal}
        onHide={() => setShowPilotModal(false)}
        pilot={selectedPilotToView}
        portfolio={selectedPilotToView?.portfolio}
        baseS3Url={selectedPilotToView?.baseS3Url}
      />
    </div>
  );
};

export default PilotOptimization;
